import React, { useEffect } from 'react';
import { useLocation, useNavigate, Outlet } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Home, Heart, Settings, Phone, MessageCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../../i18n/LanguageContext';
import { announceNavigation } from '../../utils/announce';
import { VoiceControlButton } from '../ui/VoiceControl';

// Page transition variants - simplified for reliability
const pageVariants = {
  initial: {
    opacity: 0,
  },
  enter: {
    opacity: 1,
    transition: {
      duration: 0.2,
      ease: 'easeOut',
    },
  },
  exit: {
    opacity: 0,
    transition: {
      duration: 0.1,
      ease: 'easeIn',
    },
  },
};

// Stagger children animation
const containerVariants = {
  initial: {},
  enter: {
    transition: {
      staggerChildren: 0.05,
      delayChildren: 0.1,
    },
  },
};

const itemVariants = {
  initial: { opacity: 0, y: 10 },
  enter: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.3,
      ease: [0.25, 0.1, 0.25, 1],
    },
  },
};

// Export variants for use in pages
export { containerVariants, itemVariants, pageVariants };

// Animated Outlet wrapper for page transitions
interface AnimatedOutletProps {
  children: React.ReactNode;
}

export const AnimatedPage: React.FC<AnimatedOutletProps> = ({ children }) => {
  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="enter"
      exit="exit"
    >
      {children}
    </motion.div>
  );
};

export const SeniorLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === '/';
  const { t, isRTL } = useLanguage();
  
  // Announce page changes for screen readers
  useEffect(() => {
    const pageNames: Record<string, string> = {
      '/senior': 'Home',
      '/senior/chat': 'AI Chat',
      '/senior/games': 'Brain Games',
      '/senior/reminders': 'Reminders',
      '/senior/mood': 'Mood Tracker',
      '/senior/exercises': 'Exercises',
      '/senior/about': 'About',
      '/senior/settings': 'Settings',
      '/senior/wisdom': 'Daily Wisdom',
      '/senior/health': 'Health Resources',
      '/senior/family': 'Family Sharing',
      '/senior/hobbies': 'Hobbies',
      '/senior/privacy': 'Privacy Policy',
      '/senior/terms': 'Terms of Service',
      '/senior/dashboard': 'Dashboard',
    };
    const pageName = pageNames[location.pathname] || 'Page';
    announceNavigation(pageName);
  }, [location.pathname]);
  
  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
    // Also scroll the main content container if it exists
    const mainContent = document.getElementById('main-content');
    if (mainContent) {
      mainContent.scrollTo({ top: 0, behavior: 'instant' });
    }
  }, [location.pathname]);
  
  // Use correct arrow direction based on RTL
  const BackArrow = isRTL ? ArrowRight : ArrowLeft;

  const navItems = [
      { path: '/senior', icon: Home, label: t('home') },
      { path: '/senior/about', icon: Heart, label: t('about') },
      { path: '/senior/settings', icon: Settings, label: t('settings') },
      { path: '/senior/chat-family', icon: MessageCircle, label: t('ccChat') },
  ];

  return (
    <div
      data-layout="wrap"
      className="min-h-dvh flex flex-col overflow-x-hidden w-full max-w-md mx-auto bg-background border-x border-accent/20 shadow-2xl pb-[env(safe-area-inset-bottom)]"
    >
      {/* Elegant Header with rounded bottom corners */}
      <header
        className="bg-surface/80 backdrop-blur-sm p-3 border-b border-accent/20 flex flex-col gap-2 sticky top-0 z-10 rounded-b-3xl shadow-warm flex-shrink-0"
              style={{ overflow: 'visible' }}
      >
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center gap-3">
            {!isHome && (
              <motion.button 
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0, opacity: 0 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate(-1)}
                className="p-2 bg-accent/20 rounded-2xl text-primary hover:bg-accent/30 transition-colors"
                aria-label="Go Back"
              >
                <ArrowLeft size={24} strokeWidth={2.5} className={isRTL ? 'rtl-flip' : ''} />
              </motion.button>
            )}
            <div>
              <h1 className="header-text font-bold text-primary font-serif" style={{ fontSize: '2.8rem', lineHeight: '1', whiteSpace: 'nowrap' }}>DIPPS</h1>
            </div>
          </div>
          
          {/* Emergency Button - Opens Phone Dialer */}
          <div className="flex items-center">
            <motion.a 
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              href="tel:911"
              className="flex items-center gap-1 px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-full shadow-lg transition-colors"
              aria-label={t('emergency')}
            >
              <Phone size={18} strokeWidth={2.5} />
              <span className="text-sm font-bold">911</span>
            </motion.a>
          </div>
        </div>
      </header>

      {/* Main Content with Page Transitions */}
      {/* Header: ~60px, Footer nav: ~80px, pb-28: ~112px padding - ensure content fills viewport */}
      <main id="main-content" className="flex-1 w-full overflow-x-hidden min-h-0" role="main" aria-label="Main content">
        <Outlet />

        {/* Footer Email */}
        <div className="mt-8 text-center">
          <a href="mailto:support@dipps.ca" className="text-xs text-text-muted hover:text-primary transition-colors">
            support@dipps.ca
          </a>
        </div>
      </main>

      {/* Voice Control Button */}
      <VoiceControlButton />

      {/* Elegant Bottom Navigation with Animations */}
      <footer className="mt-auto">
        <nav
          className="z-50 bg-dark-matter border-t border-border p-4"
          role="navigation"
          aria-label="Main navigation"
        >
          <div className="flex justify-around items-center">
            {navItems.map((item, index) => {
              const isActive = location.pathname === item.path;
              return (
                <motion.button 
                  key={item.path}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 + index * 0.1 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => navigate(item.path)}
                  className={`flex flex-col items-center gap-1 p-2 rounded-2xl transition-all ${
                    isActive
                      ? 'text-primary bg-accent/20' 
                      : 'text-text-muted hover:text-primary'
                  }`}
                  aria-label={item.label}
                  aria-current={isActive ? 'page' : undefined}
                >
                  <motion.span 
                    className="icon-container-lg flex items-center justify-center"
                    animate={isActive ? { scale: [1, 1.1, 1] } : {}}
                    transition={{ duration: 0.3 }}
                  >
                    <item.icon size={28} strokeWidth={isActive ? 2.5 : 2} aria-hidden="true" />
                  </motion.span>
                  <span className="text-xs font-medium">{item.label}</span>
                </motion.button>
              );
            })}
          </div>
        </nav>
      </footer>
    </div>
  );
};

export default SeniorLayout;
